import { createContext, useContext, useState, useMemo } from 'react'
import { supabase } from '../utils/supabase'

// Create a stable reference for the context
const AuthContext = createContext({})

export const useAuth = () => {
  return useContext(AuthContext)
}

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Removed debug log that made remounts visible

  const signInWithGoogle = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/admin`
        }
      });
      if (error) throw error;
    } catch (error) {
      console.error('[AuthContext] Google sign in error:', error);
      setError(error.message);
      throw error;
    }
  }

  const signInWithEmail = async (email, password) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[AuthContext] Email sign in error:', error);
      setError(error.message);
      throw error;
    }
  }

  const signUpWithEmail = async (email, password, invitationToken = null) => {
    try {
      setError(null);
      
      // Check if this is an invited user by looking at the URL or passed token
      const urlParams = new URLSearchParams(window.location.search);
      const token = invitationToken || urlParams.get('token');
      
      let signupOptions = {
        emailRedirectTo: `${window.location.origin}/admin`
      };
      
      // If user has an invitation token, skip email confirmation
      if (token) {
        console.log('[AuthContext] Signup with invitation token - skipping email confirmation');
        signupOptions.emailRedirectTo = `${window.location.origin}/admin`;
        // For invited users, we want to auto-confirm their email
        // This requires using the admin API on the backend
        
        // First, try to verify the invitation token is valid
        try {
          const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
          const response = await fetch(`${apiBaseUrl}/api/invitations/verify-token/${token}`);
          
          if (response.ok) {
            const tokenData = await response.json();
            console.log('[AuthContext] Valid invitation token found:', tokenData);
            
            // Create user with admin API to bypass email confirmation
            const signupResponse = await fetch(`${apiBaseUrl}/api/auth/signup-with-invitation`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                email,
                password,
                token
              })
            });
            
            if (signupResponse.ok) {
              const result = await signupResponse.json();
              console.log('[AuthContext] User created successfully with invitation:', result);
              
              // Now sign them in immediately
              const signInResult = await signInWithEmail(email, password);
              return { success: true, data: signInResult };
            } else {
              const error = await signupResponse.json();
              throw new Error(error.message || 'Failed to create account with invitation');
            }
          } else {
            console.warn('[AuthContext] Invalid invitation token, using normal signup flow');
          }
        } catch (inviteError) {
          console.warn('[AuthContext] Error verifying invitation token:', inviteError);
          // Fall back to normal signup if invitation validation fails
        }
      }
      
      // Regular signup flow for non-invited users
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: signupOptions
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[AuthContext] Sign up error:', error);
      setError(error.message);
      throw error;
    }
  }

  const signOut = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setSession(null);
    } catch (error) {
      console.error('[AuthContext] Sign out error:', error);
      setError(error.message);
      throw error;
    }
  }

  const resetPassword = async (email) => {
    try {
      setError(null);
      // Check if user exists and what auth provider they use via backend API
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const checkResponse = await fetch(`${apiBaseUrl}/api/auth/check-auth-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      if (checkResponse.ok) {
        const authInfo = await checkResponse.json();
        if (authInfo.googleOnly) {
          throw new Error('This account uses Google sign-in. Please use the "Sign in with Google" button instead.');
        }
      }
      // Call your backend to send the reset email
      const resetResponse = await fetch(`${apiBaseUrl}/api/auth/reset-password-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      const resetData = await resetResponse.json();
      if (!resetResponse.ok || !resetData.success) {
        throw new Error(resetData.error || 'Failed to send password reset email.');
      }
      return resetData;
    } catch (error) {
      console.error('[AuthContext] Password reset error:', error);
      setError(error.message);
      throw error;
    }
  }

  // CRITICAL FIX: USE useMemo to prevent unnecessary re-renders
  const value = useMemo(() => ({
    session,
    user,
    loading,
    error,
    setError,
    setSession,
    setUser,
    setLoading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    resetPassword
  }), [session, user, loading, error]);

  // Use React.memo to prevent unnecessary re-renders of children
  const MemoizedChildren = useMemo(() => children, []);

  return (
    <AuthContext.Provider value={value}>
      {MemoizedChildren}
    </AuthContext.Provider>
  )
} 